import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, RotateCcw, CalendarClock, ClipboardList, Loader2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const fmtDate = (v) => {
    if (!v) return "";
    const d = new Date(v);
    return isNaN(d) ? String(v) : d.toLocaleDateString("de-DE");
};

/**
 * CompanyTasksPanel — Aufgaben-Inbox des Mitarbeiters im Firmen-Bereich:
 * zugewiesene Aufgaben (delegated_tasks) + anstehende Werkstatt-Termine.
 * Erledigte sind per Filter sichtbar und wieder aktivierbar.
 */
export default function CompanyTasksPanel({ dealerId }) {
    const navigate = useNavigate();
    const [tasks, setTasks] = useState([]);
    const [appts, setAppts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDone, setShowDone] = useState(false);
    const [busy, setBusy] = useState(null);
    // Jul-2026 (User-Wunsch) — gleiche 10/20/50-Blöcke wie im Mitarbeiter-Dashboard.
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    useEffect(() => { setPage(0); }, [showDone]);

    const load = useCallback(() => {
        setLoading(true);
        api.get(`/vermittler/company/${dealerId}/tasks${showDone ? "?include_done=1" : ""}`)
            .then((r) => { setTasks(r.data.tasks || []); setAppts(r.data.appointments || []); })
            .catch(() => { setTasks([]); setAppts([]); })
            .finally(() => setLoading(false));
    }, [dealerId, showDone]);

    useEffect(() => { load(); }, [load]);

    const act = async (taskId, action) => {
        setBusy(taskId);
        try {
            await api.post(`/me/tasks/${taskId}/${action}`);
            toast.success(action === "done" ? "Aufgabe als erledigt markiert" : "Aufgabe wieder geöffnet");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen");
        } finally {
            setBusy(null);
        }
    };

    const openTasks = tasks.filter((t) => t.status === "open");
    const doneTasks = tasks.filter((t) => t.status === "done");
    const pages = Math.max(1, Math.ceil(openTasks.length / pageSize));
    const safePage = Math.min(page, pages - 1);
    const pagedOpen = openTasks.slice(safePage * pageSize, (safePage + 1) * pageSize);

    // Deep-Link einer Aufgabe (User-Wunsch): cta_url (interner Pfad) oder Inserat.
    const taskTarget = (t) => t.cta_url || (t.listing_id ? `/edit-listing/${t.listing_id}` : null);
    const openTask = (t) => {
        const target = taskTarget(t);
        if (!target) return;
        if (/^https?:\/\//i.test(target)) window.open(target, "_blank", "noopener");
        else navigate(target);
    };

    return (
        <div className="bg-white border border-gray-200 rounded-sm" data-testid="company-tasks-panel">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
                <ClipboardList className="w-4 h-4 text-[#FFCC00]" />
                <h3 className="font-display font-bold text-sm">Meine Aufgaben</h3>
                {openTasks.length > 0 && (
                    <span className="text-[10px] uppercase tracking-wider font-bold bg-red-500 text-white px-1.5 py-0.5 rounded-full font-mono-num" data-testid="company-tasks-open-count">{openTasks.length}</span>
                )}
                <label className="ml-auto flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                    <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} className="w-3.5 h-3.5 accent-[#FFCC00]" data-testid="company-tasks-show-done" />
                    Erledigte anzeigen
                </label>
            </div>

            <div className="p-4 space-y-4">
                {loading ? (
                    <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" /></div>
                ) : (
                    <>
                        {openTasks.length === 0 && doneTasks.length === 0 && appts.length === 0 && (
                            <div className="text-sm text-gray-500 text-center py-4" data-testid="company-tasks-empty">Keine offenen Aufgaben oder Termine. 👍</div>
                        )}

                        {openTasks.length > 0 && (
                            <>
                            <div className="flex items-center gap-2 flex-wrap" data-testid="company-tasks-pagination-controls">
                                <span className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">Pro Seite:</span>
                                {[10, 20, 50].map((n) => (
                                    <button
                                        key={n}
                                        type="button"
                                        onClick={() => { setPageSize(n); setPage(0); }}
                                        className={`text-[10px] font-bold px-2 py-0.5 rounded-sm border ${pageSize === n ? "bg-[#111827] text-[#FFCC00] border-[#111827]" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}
                                        data-testid={`company-tasks-pagesize-${n}`}
                                    >
                                        {n}
                                    </button>
                                ))}
                                <span className="text-[10px] text-gray-500 font-mono-num ml-1" data-testid="company-tasks-page-info">
                                    {safePage * pageSize + 1}–{Math.min((safePage + 1) * pageSize, openTasks.length)} von {openTasks.length}
                                </span>
                                {pages > 1 && (
                                    <span className="inline-flex items-center gap-1">
                                        <button type="button" disabled={safePage === 0} onClick={() => setPage(safePage - 1)} className="text-[10px] font-bold px-2 py-0.5 rounded-sm border border-gray-300 bg-white disabled:opacity-40" data-testid="company-tasks-page-prev">‹</button>
                                        <button type="button" disabled={safePage >= pages - 1} onClick={() => setPage(safePage + 1)} className="text-[10px] font-bold px-2 py-0.5 rounded-sm border border-gray-300 bg-white disabled:opacity-40" data-testid="company-tasks-page-next">›</button>
                                    </span>
                                )}
                            </div>
                            <ul className="space-y-2">
                                {pagedOpen.map((t) => {
                                    const target = taskTarget(t);
                                    return (
                                    <li key={t.id} className="flex items-start gap-3 border border-gray-200 rounded-sm p-3 hover:border-[#FFCC00] transition-colors" data-testid={`company-task-${t.id}`}>
                                        <button
                                            type="button"
                                            onClick={() => target && openTask(t)}
                                            disabled={!target}
                                            className={`flex-1 min-w-0 text-left ${target ? "cursor-pointer" : "cursor-default"}`}
                                            data-testid={`company-task-open-${t.id}`}
                                            title={target ? "Öffnen" : undefined}
                                        >
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className={`font-bold text-sm text-[#111827] ${target ? "group-hover:underline" : ""}`}>{t.title}</span>
                                                {t.area && <span className="text-[10px] uppercase tracking-wider font-bold bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-sm">{t.area}</span>}
                                                {t.overdue && <span className="text-[10px] uppercase tracking-wider font-bold bg-red-100 text-red-700 px-1.5 py-0.5 rounded-sm" data-testid={`company-task-overdue-${t.id}`}>Überfällig</span>}
                                                {target && <ChevronRight className="w-3.5 h-3.5 text-[#FFCC00] shrink-0" />}
                                            </div>
                                            {t.body && <p className="text-xs text-gray-600 mt-0.5">{t.body}</p>}
                                            {t.due_at && <p className="text-[11px] text-gray-400 mt-0.5">Frist: {fmtDate(t.due_at)}</p>}
                                        </button>
                                        <button onClick={() => act(t.id, "done")} disabled={busy === t.id} className="inline-flex items-center gap-1 text-xs font-bold text-green-700 hover:text-green-900 shrink-0" data-testid={`company-task-done-${t.id}`}>
                                            <CheckCircle2 className="w-4 h-4" /> Erledigt
                                        </button>
                                    </li>
                                    );
                                })}
                            </ul>
                            </>
                        )}

                        {showDone && doneTasks.length > 0 && (
                            <div>
                                <div className="text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-2">Erledigt</div>
                                <ul className="space-y-2">
                                    {doneTasks.map((t) => (
                                        <li key={t.id} className="flex items-center gap-3 border border-gray-100 bg-gray-50 rounded-sm p-3" data-testid={`company-task-done-row-${t.id}`}>
                                            <span className="flex-1 min-w-0 text-sm text-gray-500 line-through truncate">{t.title}</span>
                                            <button onClick={() => act(t.id, "reopen")} disabled={busy === t.id} className="inline-flex items-center gap-1 text-xs font-bold text-[#111827] hover:text-[#FFCC00] shrink-0" data-testid={`company-task-reopen-${t.id}`}>
                                                <RotateCcw className="w-3.5 h-3.5" /> Wieder öffnen
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {appts.length > 0 && (
                            <div>
                                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-2">
                                    <CalendarClock className="w-3.5 h-3.5" /> Anstehende Termine
                                </div>
                                <ul className="divide-y divide-gray-100 border border-gray-200 rounded-sm">
                                    {appts.slice(0, 10).map((a) => (
                                        <li key={a.id} className="flex items-center gap-3 px-3 py-2" data-testid={`company-appointment-${a.id}`}>
                                            <div className="text-center shrink-0">
                                                <div className="text-xs font-bold font-mono-num">{fmtDate(a.date)}</div>
                                                <div className="text-[10px] text-gray-400">{a.time}</div>
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm font-bold truncate">{a.service || "Termin"}</div>
                                                <div className="text-[11px] text-gray-500 truncate">{a.customer} · {a.vehicle}</div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
